import {
  Box,
  Container,
  Heading,
  Text,
  VStack,
  Button,
  SimpleGrid,
  Flex,
  Icon,
  Badge,
  Card,
  CardHeader,
  CardBody,
  List,
  ListItem,
  ListIcon,
  useColorMode,
  Divider,
  Image,
} from "@chakra-ui/react";
import {
  CheckIcon,
  ChevronRightIcon,
  WarningIcon,
  StarIcon,
} from "@chakra-ui/icons";
import {
  FaBrain,
  FaShieldAlt,
  FaCoins,
  FaUsers,
  FaChartLine,
  FaLock,
  FaRocket,
  FaHandshake,
  FaBalanceScale,
  FaCrown,
  FaFire,
} from "react-icons/fa";
import { useNavigate } from "react-router-dom";
import { NavbarWithCallToAction } from "../../components/chakra/NavbarWithCallToAction/NavbarWithCallToAction";
import { FooterWithFourColumns } from "../../components/chakra/FooterWithFourColumns/FooterWithFourColumns";
import { getColor, getBrandValue } from "../../brandConfig";

const BusinessBuilder = () => {
  const navigate = useNavigate();
  const { colorMode } = useColorMode();

  const bg = getColor("background.main", colorMode);
  const cardGradientBg =
    colorMode === "light"
      ? `linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 248, 248, 0.9) 50%, rgba(255, 255, 255, 0.95) 100%)`
      : `linear-gradient(135deg, rgba(20, 20, 20, 0.8) 0%, rgba(30, 30, 30, 0.6) 50%, rgba(20, 20, 20, 0.8) 100%)`;
  const cardBorder =
    colorMode === "light" ? "rgba(0, 0, 0, 0.1)" : "rgba(255, 255, 255, 0.1)";
  const textPrimary = getColor(
    colorMode === "light" ? "text.primary" : "text.primaryDark",
    colorMode
  );
  const textSecondary = getColor(
    colorMode === "light" ? "text.secondary" : "text.secondaryDark",
    colorMode
  );
  const textMuted = getColor(
    colorMode === "light" ? "text.muted" : "text.mutedDark",
    colorMode
  );
  const accentBlue = getColor("primary", colorMode);

  return (
    <Box bg={bg} minHeight="100vh">
      <NavbarWithCallToAction />

      <Container maxW="800px" py={16}>
        <VStack spacing={16} align="stretch">
          {/* Hero Section */}
          <Box textAlign="center" py={12}>
            <Badge
              bg="linear-gradient(135deg, #9333EA 0%, #7C3AED 100%)"
              color="white"
              fontSize="md"
              px={6}
              py={2}
              borderRadius="full"
              fontWeight="600"
              mb={6}
              boxShadow="0 4px 16px rgba(147, 51, 234, 0.4)"
            >
              <Icon as={FaFire} mr={2} />
              The Train Is Leaving the Station
            </Badge>
            <Heading
              as="h1"
              fontSize={{ base: "4xl", md: "6xl", lg: "7xl" }}
              fontWeight="800"
              letterSpacing="-0.03em"
              mb={6}
              color={textPrimary}
              bgGradient="linear(to-r, #9333EA, #7C3AED, #6366F1)"
              bgClip="text"
            >
              Business Builder System
            </Heading>
            <Text
              fontSize={{ base: "xl", md: "2xl" }}
              mb={8}
              color={textSecondary}
              maxW="900px"
              mx="auto"
              lineHeight="1.7"
            >
              The revolutionary platform that makes YOU sovereign over your
              business data, trains AI to represent YOUR interests, and lets you
              participate in the platform's growth through tokenized ownership.
            </Text>
            <Flex
              justify="center"
              gap={4}
              flexWrap="wrap"
              align="center"
              mb={4}
            >
              <Button
                size="lg"
                bg="linear-gradient(135deg, #9333EA 0%, #7C3AED 100%)"
                color="white"
                rightIcon={<ChevronRightIcon />}
                _hover={{
                  transform: "translateY(-2px)",
                  boxShadow: "0 8px 24px rgba(147, 51, 234, 0.5)",
                }}
                px={10}
                py={7}
                fontSize="xl"
                onClick={() => navigate("/live-event/interest")}
              >
                Get Started — Invest Now
              </Button>
              <Button
                size="lg"
                variant="outline"
                borderWidth="2px"
                borderColor="#9333EA"
                color="#9333EA"
                _hover={{
                  bg: "rgba(147, 51, 234, 0.1)",
                }}
                px={10}
                py={7}
                fontSize="xl"
                onClick={() => navigate("/businessbuilder/tokenomics")}
              >
                Learn More
              </Button>
            </Flex>
            <Text fontSize="sm" color={textMuted} fontStyle="italic">
              Earn or invest in BB tokens • Monthly cashflow distributions •
              Governance rights
            </Text>
          </Box>

          {/* The Problem Section */}
          <Card
            bg={cardGradientBg}
            backdropFilter="blur(10px)"
            boxShadow="0 8px 32px 0 rgba(0, 0, 0, 0.37)"
            borderRadius="2xl"
            borderWidth="2px"
            borderColor="rgba(239, 68, 68, 0.4)"
          >
            <CardHeader
              bg="linear-gradient(135deg, rgba(239, 68, 68, 0.2) 0%, rgba(220, 38, 38, 0.2) 100%)"
              borderBottomWidth="1px"
              borderColor={cardBorder}
            >
              <Flex align="center">
                <Icon as={WarningIcon} w={7} h={7} color="#EF4444" mr={4} />
                <Heading size="lg" color={textPrimary}>
                  The Problem: You're Building Someone Else's Empire
                </Heading>
              </Flex>
            </CardHeader>
            <CardBody p={8}>
              <SimpleGrid columns={{ base: 1, md: 2 }} spacing={8}>
                <Box>
                  <Heading size="md" mb={4} color="#EF4444">
                    Traditional SaaS Is Broken
                  </Heading>
                  <List spacing={3}>
                    <ListItem color={textSecondary}>
                      <ListIcon as={WarningIcon} color="#EF4444" />
                      <strong>They Own Your Data</strong> — Your customer lists, business processes, and intellectual property belong to them
                    </ListItem>
                    <ListItem color={textSecondary}>
                      <ListIcon as={WarningIcon} color="#EF4444" />
                      <strong>They Train AI on Your Business</strong> — Google, Facebook, and Salesforce use YOUR data to make THEIR AI smarter
                    </ListItem>
                    <ListItem color={textSecondary}>
                      <ListIcon as={WarningIcon} color="#EF4444" />
                      <strong>Vendor Lock-In Forever</strong> — Switch platforms? Lose everything. You're trapped.
                    </ListItem>
                    <ListItem color={textSecondary}>
                      <ListIcon as={WarningIcon} color="#EF4444" />
                      <strong>Subscription Fees Never End</strong> — You pay monthly, quarterly, yearly... forever. No ownership.
                    </ListItem>
                  </List>
                </Box>
                <Box
                  bg="rgba(239, 68, 68, 0.1)"
                  p={6}
                  borderRadius="lg"
                  borderWidth="2px"
                  borderColor="rgba(239, 68, 68, 0.3)"
                >
                  <Heading size="sm" color="#EF4444" mb={4}>
                    Here's What's Really Happening:
                  </Heading>
                  <Text fontSize="md" color={textSecondary} mb={3}>
                    You spend years building your business on their platform.
                    Every customer interaction, every sale, every insight goes
                    into THEIR database.
                  </Text>
                  <Text fontSize="md" color={textSecondary} mb={3}>
                    Their AI learns from your business. Then they sell that
                    intelligence to your competitors. You trained their system.
                    They profit.
                  </Text>
                  <Text
                    fontSize="lg"
                    fontWeight="700"
                    color="#EF4444"
                    fontStyle="italic"
                  >
                    You're not the customer. You're the product.
                  </Text>
                </Box>
              </SimpleGrid>
            </CardBody>
          </Card>

          {/* The Solution Section */}
          <Card
            bg={cardGradientBg}
            backdropFilter="blur(10px)"
            boxShadow="0 8px 32px 0 rgba(0, 0, 0, 0.37)"
            borderRadius="2xl"
            borderWidth="2px"
            borderColor="rgba(34, 197, 94, 0.4)"
          >
            <CardHeader
              bg="linear-gradient(135deg, rgba(34, 197, 94, 0.2) 0%, rgba(16, 185, 129, 0.2) 100%)"
              borderBottomWidth="1px"
              borderColor={cardBorder}
            >
              <Flex align="center">
                <Icon as={FaShieldAlt} w={7} h={7} color="#10B981" mr={4} />
                <Heading size="lg" color={textPrimary}>
                  The Solution: Business Sovereignty
                </Heading>
              </Flex>
            </CardHeader>
            <CardBody p={8}>
              <Text fontSize="xl" mb={6} color={textSecondary} lineHeight="1.8">
                Business Builder is the <strong>antidote to platform
                dependence</strong>. It's infrastructure you control, data you
                own, and AI that works for YOU.
              </Text>
              <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6}>
                {[
                  {
                    icon: FaLock,
                    title: "Own Your Data",
                    desc: "Your MongoDB, your IPFS, your infrastructure. Export anytime, no permission needed.",
                    color: "#3B82F6",
                  },
                  {
                    icon: FaBrain,
                    title: "Train Your AI",
                    desc: "AI learns from YOUR business only. Represents YOUR interests, not Google's.",
                    color: "#9333EA",
                  },
                  {
                    icon: FaRocket,
                    title: "Scale Freely",
                    desc: "No vendor lock-in. Switch, expand, customize. You're in control.",
                    color: "#F59E0B",
                  },
                  {
                    icon: FaCoins,
                    title: "Own the Platform",
                    desc: "Buy BB tokens. Participate in profit distributions. Vote on features.",
                    color: "#10B981",
                  },
                ].map((item, index) => (
                  <Box
                    key={index}
                    p={5}
                    bg={`rgba(${item.color === "#3B82F6" ? "59, 130, 246" : item.color === "#9333EA" ? "147, 51, 234" : item.color === "#F59E0B" ? "245, 158, 11" : "16, 185, 129"}, 0.1)`}
                    borderRadius="lg"
                    borderWidth="1px"
                    borderColor={`${item.color}40`}
                    textAlign="center"
                  >
                    <Icon as={item.icon} w={10} h={10} color={item.color} mb={3} />
                    <Heading size="sm" mb={2} color={textPrimary}>
                      {item.title}
                    </Heading>
                    <Text fontSize="sm" color={textSecondary}>
                      {item.desc}
                    </Text>
                  </Box>
                ))}
              </SimpleGrid>
            </CardBody>
          </Card>

          {/* The Journey Section */}
          <Box textAlign="center">
            <Heading size="xl" mb={4} color={textPrimary}>
              The Journey to Business Sovereignty
            </Heading>
            <Text fontSize="lg" mb={10} color={textSecondary} maxW="800px" mx="auto">
              Business Builder isn't just software. It's a transformation from
              platform dependence to business sovereignty.
            </Text>
            <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6}>
              {[
                {
                  phase: "Phase 1",
                  title: "Centralize",
                  desc: "All business operations in ONE platform. Clients, projects, billing, calendar, communication.",
                  icon: "1️⃣",
                },
                {
                  phase: "Phase 2",
                  title: "Own Your Data",
                  desc: "IPFS file storage, MongoDB database, API access. Your data, your infrastructure.",
                  icon: "2️⃣",
                },
                {
                  phase: "Phase 3",
                  title: "Train Your AI",
                  desc: "AI learns from YOUR business data. Voice assistants, automation, intelligence — all yours.",
                  icon: "3️⃣",
                },
                {
                  phase: "Phase 4",
                  title: "AI Represents You",
                  desc: "Automated customer service, scheduling, billing. You're at the center, AI is your workforce.",
                  icon: "4️⃣",
                },
              ].map((item, index) => (
                <Card
                  key={index}
                  bg={cardGradientBg}
                  borderWidth="1px"
                  borderColor={cardBorder}
                  boxShadow="0 4px 16px rgba(0, 0, 0, 0.2)"
                >
                  <CardBody p={6} textAlign="center">
                    <Text fontSize="4xl" mb={2}>
                      {item.icon}
                    </Text>
                    <Badge colorScheme="purple" mb={2}>
                      {item.phase}
                    </Badge>
                    <Heading size="md" mb={3} color={textPrimary}>
                      {item.title}
                    </Heading>
                    <Text fontSize="sm" color={textSecondary}>
                      {item.desc}
                    </Text>
                  </CardBody>
                </Card>
              ))}
            </SimpleGrid>
          </Box>

          {/* Tokenization Section */}
          <Card
            bg="linear-gradient(135deg, rgba(147, 51, 234, 0.1) 0%, rgba(79, 70, 229, 0.1) 100%)"
            backdropFilter="blur(10px)"
            boxShadow="0 12px 40px rgba(147, 51, 234, 0.3)"
            borderRadius="2xl"
            borderWidth="3px"
            borderColor="rgba(147, 51, 234, 0.5)"
            position="relative"
            overflow="hidden"
          >
            <Box
              position="absolute"
              top="-50px"
              right="-50px"
              width="200px"
              height="200px"
              bg="radial-gradient(circle, rgba(147, 51, 234, 0.3), transparent)"
              borderRadius="full"
              filter="blur(40px)"
            />
            <CardBody p={10} position="relative" zIndex={1}>
              <Flex align="center" mb={6} justify="center">
                <Icon as={FaCoins} w={12} h={12} color="#F59E0B" mr={4} />
                <Heading size="2xl" color={textPrimary}>
                  The Business Builder Token (BB)
                </Heading>
              </Flex>
              <Text
                fontSize="xl"
                textAlign="center"
                mb={8}
                color={textSecondary}
                maxW="900px"
                mx="auto"
              >
                Here's where it gets revolutionary: You don't just USE the
                platform. You can OWN part of it.
              </Text>
              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={8}>
                <Box
                  bg="rgba(255, 255, 255, 0.1)"
                  p={6}
                  borderRadius="xl"
                  textAlign="center"
                >
                  <Icon as={FaChartLine} w={10} h={10} color="#10B981" mb={3} />
                  <Heading size="md" mb={3} color={textPrimary}>
                    Monthly Profit Sharing
                  </Heading>
                  <Text fontSize="sm" color={textSecondary} mb={3}>
                    Hold BB tokens, receive monthly cashflow distributions based
                    on platform revenue.
                  </Text>
                  <Text fontSize="xs" fontStyle="italic" color={textMuted}>
                    Example: 10,000 BB tokens at $10K profit = $70/month
                  </Text>
                </Box>
                <Box
                  bg="rgba(255, 255, 255, 0.1)"
                  p={6}
                  borderRadius="xl"
                  textAlign="center"
                >
                  <Icon as={FaBalanceScale} w={10} h={10} color="#3B82F6" mb={3} />
                  <Heading size="md" mb={3} color={textPrimary}>
                    Governance Rights
                  </Heading>
                  <Text fontSize="sm" color={textSecondary} mb={3}>
                    Vote on feature priorities, budget allocations, and platform
                    direction via Snapshot.
                  </Text>
                  <Text fontSize="xs" fontStyle="italic" color={textMuted}>
                    1 BB token = 1 vote. Your voice matters.
                  </Text>
                </Box>
                <Box
                  bg="rgba(255, 255, 255, 0.1)"
                  p={6}
                  borderRadius="xl"
                  textAlign="center"
                >
                  <Icon as={FaCrown} w={10} h={10} color="#F59E0B" mb={3} />
                  <Heading size="md" mb={3} color={textPrimary}>
                    One Unified Token
                  </Heading>
                  <Text fontSize="sm" color={textSecondary} mb={3}>
                    Earn BB through contribution, purchase directly via investment tiers, or top up at the current reward rate. Use as bill discounts or hold for distributions.
                  </Text>
                  <Text fontSize="xs" fontStyle="italic" color={textMuted}>
                    Earn it, buy it, use it — one token, multiple utilities
                  </Text>
                </Box>
              </SimpleGrid>
              <Divider my={8} />
              <Box textAlign="center">
                <Heading size="md" mb={4} color={textPrimary}>
                  Why This Matters
                </Heading>
                <Text fontSize="md" color={textSecondary} mb={6} maxW="800px" mx="auto">
                  Tom has invested <strong>thousands of hours</strong> building this
                  infrastructure. The email systems, module architecture, AI
                  integration, and continuous development benefit ALL tenants.
                  The tokenization model finally recognizes this value and lets
                  YOU participate in the upside.
                </Text>
                <Button
                  size="lg"
                  bg="linear-gradient(135deg, #F59E0B 0%, #D97706 100%)"
                  color="white"
                  rightIcon={<ChevronRightIcon />}
                  _hover={{
                    transform: "translateY(-2px)",
                    boxShadow: "0 8px 24px rgba(245, 158, 11, 0.5)",
                  }}
                  px={10}
                  py={7}
                  fontSize="xl"
                  onClick={() => navigate("/businessbuilder/tokenomics")}
                >
                  View Token Economics
                </Button>
              </Box>
            </CardBody>
          </Card>

          {/* Token Top-Up Section */}
          <Card
            bg={cardGradientBg}
            backdropFilter="blur(10px)"
            boxShadow="0 8px 32px 0 rgba(0, 0, 0, 0.37)"
            borderRadius="2xl"
            borderWidth="1px"
            borderColor={cardBorder}
          >
            <CardBody p={8}>
              <Flex align="center" mb={4} justify="center">
                <Icon as={FaCoins} w={8} h={8} color="#10B981" mr={3} />
                <Heading size="lg" color={textPrimary}>
                  Top Up Your BB Tokens
                </Heading>
              </Flex>
              <Text fontSize="md" textAlign="center" mb={6} color={textSecondary} maxW="700px" mx="auto">
                Already a subscriber? Purchase additional BB tokens at your tenant's configured reward rate. Tokens can be used as bill discounts, held for profit distributions, or used for governance voting.
              </Text>
              <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6} mb={6}>
                <Box textAlign="center" p={4} bg="rgba(16, 185, 129, 0.1)" borderRadius="lg">
                  <Text fontSize="sm" color={textMuted} mb={1}>Default Rate</Text>
                  <Text fontSize="2xl" fontWeight="700" color="#10B981">AU$50</Text>
                  <Text fontSize="xs" color={textSecondary}>per BB token</Text>
                </Box>
                <Box textAlign="center" p={4} bg="rgba(59, 130, 246, 0.1)" borderRadius="lg">
                  <Text fontSize="sm" color={textMuted} mb={1}>Use As</Text>
                  <Text fontSize="md" fontWeight="600" color="#3B82F6">Bill Discount</Text>
                  <Text fontSize="xs" color={textSecondary}>Apply to any invoice</Text>
                </Box>
                <Box textAlign="center" p={4} bg="rgba(147, 51, 234, 0.1)" borderRadius="lg">
                  <Text fontSize="sm" color={textMuted} mb={1}>Or Hold For</Text>
                  <Text fontSize="md" fontWeight="600" color="#9333EA">Distributions</Text>
                  <Text fontSize="xs" color={textSecondary}>Monthly profit sharing</Text>
                </Box>
              </SimpleGrid>
              <Flex justify="center">
                <Button
                  colorScheme="green"
                  rightIcon={<ChevronRightIcon />}
                  onClick={() => navigate("/live-event/interest")}
                >
                  Express Interest in Token Top-Up
                </Button>
              </Flex>
            </CardBody>
          </Card>

          {/* Tom's Vision Section */}
          <Card
            bg={cardGradientBg}
            backdropFilter="blur(10px)"
            boxShadow="0 8px 32px 0 rgba(0, 0, 0, 0.37)"
            borderRadius="2xl"
            borderWidth="1px"
            borderColor={cardBorder}
          >
            <CardBody p={10}>
              <Flex
                direction={{ base: "column", md: "row" }}
                gap={8}
                align="center"
              >
                <Box flex={1}>
                  <Badge colorScheme="purple" mb={3}>
                    Founder's Message
                  </Badge>
                  <Heading size="lg" mb={4} color={textPrimary}>
                    From Tom Miller: Why I Built This
                  </Heading>
                  <Text fontSize="md" color={textSecondary} mb={4} lineHeight="1.8">
                    For years, I've watched businesses give away their power to
                    tech giants. Every email in Gmail, every customer in
                    Salesforce, every document in Google Drive — it all feeds
                    THEIR AI.
                  </Text>
                  <Text fontSize="md" color={textSecondary} mb={4} lineHeight="1.8">
                    I built Business Builder because I believe in{" "}
                    <strong style={{ color: "#9333EA" }}>
                      business sovereignty
                    </strong>
                    . You should own your data. Train AI on YOUR terms. And if
                    the platform grows, you should benefit.
                  </Text>
                  <Text
                    fontSize="lg"
                    fontStyle="italic"
                    color="#9333EA"
                    fontWeight="600"
                    p={4}
                    bg="rgba(147, 51, 234, 0.1)"
                    borderRadius="md"
                    borderWidth="1px"
                    borderColor="rgba(147, 51, 234, 0.3)"
                  >
                    "This isn't about replacing human connection. It's about
                    enhancing human dignity through technology that respects your
                    sovereignty."
                  </Text>
                </Box>
                <Box
                  minWidth={{ base: "200px", md: "300px" }}
                  height={{ base: "200px", md: "300px" }}
                  borderRadius="xl"
                  overflow="hidden"
                  boxShadow="0 8px 32px rgba(0, 0, 0, 0.3)"
                  border="3px solid"
                  borderColor="rgba(147, 51, 234, 0.5)"
                >
                  <Image
                    src="/tomprofile.jpeg"
                    alt="Tom Miller"
                    objectFit="cover"
                    width="100%"
                    height="100%"
                  />
                </Box>
              </Flex>
            </CardBody>
          </Card>

          {/* Call to Action */}
          <Box
            textAlign="center"
            bg="linear-gradient(135deg, #9333EA 0%, #7C3AED 50%, #6366F1 100%)"
            p={12}
            borderRadius="2xl"
            color="white"
          >
            <Icon as={FaFire} w={12} h={12} mb={4} />
            <Heading size="2xl" mb={4}>
              The Train Is Leaving
            </Heading>
            <Text fontSize="xl" mb={8} maxW="800px" mx="auto">
              Earn or purchase BB tokens. Monthly profit distributions. Governance
              rights. Ground floor opportunity.
            </Text>
            <Text fontSize="lg" fontWeight="700" mb={6}>
              Don't miss the boat.
            </Text>
            <Flex justify="center" gap={4} flexWrap="wrap">
              <Button
                size="lg"
                bg="white"
                color="#9333EA"
                rightIcon={<ChevronRightIcon />}
                _hover={{
                  transform: "translateY(-2px)",
                  boxShadow: "0 8px 24px rgba(255, 255, 255, 0.3)",
                }}
                px={10}
                py={7}
                fontSize="xl"
                onClick={() => navigate("/live-event/interest")}
              >
                Invest in Business Builder
              </Button>
              <Button
                size="lg"
                variant="outline"
                borderWidth="2px"
                borderColor="white"
                color="white"
                _hover={{
                  bg: "rgba(255, 255, 255, 0.1)",
                }}
                px={10}
                py={7}
                fontSize="xl"
                onClick={() => navigate("/businessbuilder/tokenomics")}
              >
                View Token Details
              </Button>
            </Flex>
          </Box>
        </VStack>
      </Container>

      <FooterWithFourColumns />
    </Box>
  );
};

export default BusinessBuilder;
